import { FunctionComponent, MouseEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCubes } from '@fortawesome/pro-solid-svg-icons';
import { faPen, faPlusMinus, faClockRotateLeft } from '@fortawesome/pro-light-svg-icons';
import { Lot, Product } from '@spike/product-model';
import { Spinner } from 'components/UI';
import Pagination from 'components/UI/Pagination';
import ToolbarButton from 'components/UI/Toolbar/ToolbarButton';
import ToolbarItem from 'components/UI/Toolbar/ToolbarItem';
import Status from '../Status';
import { ProductHeader, formatMetric } from '../StockSidebar/ProductHeader';
import moment from 'moment';
import stock from '../StockSidebar/StockSidebar.module.scss';
import styles from './LotsSidebar.module.scss';

interface LotsListProps {
    product: Product;
    lots: Array<Lot>;
    loading?: boolean;
    page: number;
    pageSize: number;
    totalRecords: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onEdit: (lot: Lot) => void;
    onAdjust: (lot: Lot) => void;
    onHistory: (lot: Lot) => void;
}

const formatExpirationDate = (expirationDate?: string | null): string => {
    if (!expirationDate) {
        return '-';
    }

    const date = moment(expirationDate);
    return date.isValid() ? date.format('MMM D, YYYY') : '-';
};

const formatCreatedAt = (createdAt?: moment.Moment | string | null): string => {
    if (!createdAt) {
        return '-';
    }

    const date = moment.isMoment(createdAt) ? createdAt : moment(createdAt);
    return date.isValid() ? date.format('MMM D, YYYY') : '-';
};

export const LotsList: FunctionComponent<LotsListProps> = ({
    product,
    lots,
    loading,
    page,
    pageSize,
    totalRecords,
    onPageChange,
    onPageSizeChange,
    onEdit,
    onAdjust,
    onHistory
}) => {
    const inventory = product.businessProduct?.inventory;
    const lowStockValue = inventory?.lowStockNotifications ? inventory.lowStockThreshold : undefined;

    const handleMenuClick = (event: MouseEvent, action: () => void) => {
        event.stopPropagation();
        action();
    };

    if (loading) {
        return (
            <div className={stock.spinnerWrap}>
                <Spinner />
            </div>
        );
    }

    return (
        <>
            <ProductHeader
                product={product}
                trackingLabel="Track by Lots"
            />
            <div className={stock.metrics}>
                <div className={stock.metricCard}>
                    <span className={stock.metricLabel}>Quantity</span>
                    <span className={stock.metricValue}>{formatMetric(product.businessProduct?.stock)}</span>
                </div>
                <div className={stock.metricCard}>
                    <span className={stock.metricLabel}>Low Stock</span>
                    <span className={stock.metricValue}>{formatMetric(lowStockValue)}</span>
                </div>
            </div>
            {lots.length === 0 ? (
                <>
                    <h3 className={stock.historySectionTitle}>Lot History</h3>
                    <div className={styles.emptyLots}>
                        <div className={stock.emptyIconWrap}>
                            <FontAwesomeIcon icon={faCubes} />
                        </div>
                        <p className={stock.emptyTitle}>No Lots Found</p>
                        <p className={styles.emptyDescription}>Click the &quot;Add Lot&quot; button to add one.</p>
                    </div>
                </>
            ) : (
                <>
                    <div className={styles.table}>
                        <div className={styles.tableHead}>
                            <span>Lot Number</span>
                            <span>Status</span>
                            <span>Expiration Date</span>
                            <span>Qty</span>
                            <span />
                        </div>
                        {lots.map(lot => (
                            <div
                                key={lot.uuid || lot.id}
                                className={styles.tableRow}
                            >
                                <div className={styles.lotNumberCell}>
                                    <span className={styles.lotNumber}>{lot.lotNumber || '-'}</span>
                                    <span className={styles.lotCreated}>Created: {formatCreatedAt(lot.createdAt)}</span>
                                </div>
                                <div className={styles.statusCell}>
                                    <span className={styles.mobileLabel}>Status</span>
                                    {lot.status ? (
                                        <Status status={lot.status} />
                                    ) : (
                                        <span className={styles.muted}>-</span>
                                    )}
                                </div>
                                <div className={styles.expirationCell}>
                                    <span className={styles.mobileLabel}>Expiration Date</span>
                                    <span>{formatExpirationDate(lot.expirationDate)}</span>
                                </div>
                                <div className={styles.quantityCell}>
                                    <span className={styles.mobileLabel}>Qty</span>
                                    <span>{lot.quantity}</span>
                                </div>
                                <div
                                    className={styles.menuCell}
                                    onClick={event => event.stopPropagation()}
                                >
                                    <ToolbarButton>
                                        <ToolbarItem
                                            icon={faPen}
                                            text="Edit"
                                            onClick={event => handleMenuClick(event, () => onEdit(lot))}
                                        />
                                        <ToolbarItem
                                            icon={faPlusMinus}
                                            text="Adjust Lot"
                                            onClick={event => handleMenuClick(event, () => onAdjust(lot))}
                                        />
                                        <ToolbarItem
                                            icon={faClockRotateLeft}
                                            text="Lot History"
                                            onClick={event => handleMenuClick(event, () => onHistory(lot))}
                                        />
                                    </ToolbarButton>
                                </div>
                            </div>
                        ))}
                    </div>
                    {totalRecords > 0 && (
                        <Pagination
                            className={styles.pagination}
                            count={totalRecords}
                            page={page}
                            pageSize={pageSize}
                            onPageChange={onPageChange}
                            onPageSizeChange={onPageSizeChange}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default LotsList;
